import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  Button,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  CircularProgress,
  Snackbar,
  Alert,
  OutlinedInput,
  FormHelperText,
  Chip
} from '@mui/material';
import { Save as SaveIcon, CloudUpload as CloudUploadIcon } from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import config from '../../config';
import './ProfileEdit.css';

const CorporateProfileEdit = () => {
  const { token, currentUser, refreshUserInfo } = useAuth();
  
  // ローディングと通知の状態
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [notification, setNotification] = useState({ open: false, type: 'success', message: '' });
  
  // プロフィールフォームデータ
  const [formData, setFormData] = useState({
    shop_name: '',
    description: '',
    website: '',
    location: '',
    email: '',
    phone_number: '',
    industry: [],
    official_line: '',
    job_posting_url: '',
    prefecture_code: '',
    businessHours: {
      start: '10:00',
      end: '22:00'
    }
  });
  
  // 都道府県データ
  const [prefectures, setPrefectures] = useState([]);
  const [selectedCities, setSelectedCities] = useState([]);
  const [cities, setCities] = useState([]);
  
  // ファイルアップロード状態
  const [logoFile, setLogoFile] = useState(null);
  const [bannerFile, setBannerFile] = useState(null);
  const [previewLogo, setPreviewLogo] = useState('');
  const [previewBanner, setPreviewBanner] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [bannerUrl, setBannerUrl] = useState('');
  
  // 業種リスト
  const industries = [
    'スタンダード',
    '激安',
    '高級',
    '待ち合わせ型',
    '学園系',
    'ギャル系',
    '人妻・熟女系',
    '未経験・素人系',
    'ぽっちゃり系',
    '即プレイ系',
    '手コキ専門',
    'SM',
    '風エス',
    'リフレ',
    '女性用風俗'
  ];
  
  // 都道府県データを取得する関数
  const fetchPrefectures = async () => {
    try {
      const response = await axios.get(`${config.API_BASE_URL}/api/areas/prefectures`);
      if (response.data) {
        setPrefectures(response.data);
      }
    } catch (err) {
      
      setError('都道府県データの取得に失敗しました。');
    }
  };
  
  // 市区町村データを取得する関数
  const fetchCities = async (prefectureCode) => {
    if (!prefectureCode) {
      setCities([]);
      return;
    }
    
    try {
      const response = await axios.get(
        `${config.API_BASE_URL}/api/areas/cities/${prefectureCode}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );
      
      if (response.data) {
        setCities(response.data);
      }
    } catch (err) {
      
      setError('市区町村データの取得に失敗しました。');
    }
  };
  
  // 初期データの取得
  useEffect(() => {
    const fetchProfileData = async () => {
      try {
        setLoading(true);
        
        // プロフィールデータを取得
        const response = await axios.get(`${config.API_BASE_URL}/api/corporate/profile`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Cache-Control': 'no-cache'
          },
          // リクエスト失敗時に詳細情報を得るために validateStatus を追加
          validateStatus: function (status) {
            
            return status < 500; // 500未満のステータスはエラーとして扱わない
          }
        });
        
        
        
        if (response.data && response.data.status === 'success') {
          const profileData = response.data.data;
          
          // フォームデータを初期化
          setFormData({
            shop_name: profileData.corporateName || '',
            description: profileData.description || '',
            location: profileData.location || '',
            prefecture_code: profileData.prefecture?.code || '',
            website: profileData.website || '',
            phone_number: profileData.phoneNumber || '',
            email: currentUser?.email || '',
            official_line: profileData.officialLine || '',
            job_posting_url: profileData.jobPostingUrl || '',
            industry: profileData.industry ? (Array.isArray(profileData.industry) ? profileData.industry : [profileData.industry]) : [],
            businessHours: {
              start: profileData.businessHours?.start || '10:00',
              end: profileData.businessHours?.end || '22:00'
            }
          });
          
          // 選択されている都市を設定
          if (profileData.cities && profileData.cities.length > 0) {
            setSelectedCities(profileData.cities.map(city => city.code));
          }
          
          // 画像URLを設定（相対パスの場合はAPI_BASE_URLを付加）
          let logoUrl = profileData.logoUrl || '';
          let bannerUrl = profileData.bannerUrl || '';
          
          // URLが「/」で始まる相対パスの場合、API_BASE_URLを付加
          if (logoUrl && logoUrl.startsWith('/')) {
            logoUrl = `${config.API_BASE_URL}${logoUrl}`;
          }
          
          if (bannerUrl && bannerUrl.startsWith('/')) {
            bannerUrl = `${config.API_BASE_URL}${bannerUrl}`;
          }
          
          setLogoUrl(logoUrl);
          setBannerUrl(bannerUrl);
          
          // 都道府県が選択されている場合、市区町村データを取得
          if (profileData.prefecture?.code) {
            fetchCities(profileData.prefecture.code);
          }
        } else {
          
          setError('プロフィール情報の取得に失敗しました。');
        }
        
        setLoading(false);
      } catch (err) {
        
        setError('プロフィールデータの取得に失敗しました。');
        setLoading(false);
      }
    };
    
    if (token) {
      fetchProfileData();
      fetchPrefectures(); // 都道府県データを取得
    }
  }, [token, currentUser]);
  
  // 都道府県変更時に市区町村データを取得
  useEffect(() => {
    if (formData.prefecture_code) {
      fetchCities(formData.prefecture_code);
    } else {
      setCities([]);
    }
  }, [formData.prefecture_code]);
  
  // フォームフィールド変更処理
  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name.includes('.')) {
      // ネストされたフィールド（businessHoursなど）の処理
      const [parent, child] = name.split('.');
      setFormData({
        ...formData,
        [parent]: {
          ...formData[parent],
          [child]: value
        }
      });
    } else {
      // 通常のフィールドの処理
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };
  
  // 業種の変更処理
  const handleIndustryChange = (event) => {
    const {
      target: { value },
    } = event;
    setFormData({
      ...formData,
      industry: typeof value === 'string' ? value.split(',') : value,
    });
  };
  
  // 市区町村選択変更処理
  const handleCityChange = (event) => {
    const {
      target: { value },
    } = event;
    // On autofill we get a stringified value.
    setSelectedCities(
      typeof value === 'string' ? value.split(',') : value,
    );
  };
  
  // ファイル選択処理
  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    
    if (!file) return;
    
    const reader = new FileReader();
    reader.onload = () => {
      if (type === 'logo') {
        setLogoFile(file);
        setPreviewLogo(reader.result);
      } else {
        setBannerFile(file);
        setPreviewBanner(reader.result);
      }
    };
    
    reader.readAsDataURL(file);
  };
  
  // 画像アップロード処理（TweetBox方式に統一）
  const uploadImage = async (file, type) => {
    try {
      if (!file) {
        return type === 'logo' ? logoUrl : bannerUrl;
      }
      
      // TweetBoxと同じ方式でBase64に変換
      const reader = new FileReader();
      
      return new Promise((resolve, reject) => {
        reader.onload = async (e) => {
          try {
            const base64Data = e.target.result;
            
            // ファイルサイズチェック
            const blob = await fetch(base64Data).then(r => r.blob());
            if (blob.size > 5 * 1024 * 1024) {
              throw new Error('画像ファイルサイズが5MBを超えています');
            }
            
            // ファイルタイプを取得
            let fileType = "image/jpeg";
            if (base64Data.startsWith('data:image/png')) {
              fileType = "image/png";
            } else if (base64Data.startsWith('data:image/gif')) {
              fileType = "image/gif";
            } else if (base64Data.startsWith('data:image/webp')) {
              fileType = "image/webp";
            }
            
            console.log('企業プロフィール画像アップロード:', {
              type: fileType,
              sizeInMB: (blob.size / (1024 * 1024)).toFixed(2),
              dataUrlLength: base64Data.length,
              uploadType: type
            });
            
            // TweetBoxと同じ形式でAPIに送信（api.phpがBase64→ファイル変換を処理）
            const response = await axios.post(
              `${config.API_BASE_URL}/api/corporate/upload-image`,
              {
                image_data: base64Data,
                image_type: fileType,
                type: type
              },
              {
                headers: {
                  Authorization: `Bearer ${token}`,
                  'Content-Type': 'application/json'
                }
              }
            );
            
            if (response.data && (response.data.image_url || response.data.url)) {
              // image_urlまたはurl（後方互換性）を取得
              const imageUrl = response.data.image_url || response.data.url;
              console.log('企業画像アップロード成功:', imageUrl);
              // 画像URLにAPI_BASE_URLを付加して完全なURLにする
              resolve(`${config.API_BASE_URL}${imageUrl}`);
            } else {
              throw new Error('画像URLが返されませんでした');
            }
          } catch (error) {
            console.error('企業画像アップロードエラー:', error);
            reject(error);
          }
        };
        
        reader.onerror = () => {
          console.error('ファイル読み込みエラー');
          reject(new Error('ファイルの読み込みに失敗しました'));
        };
        
        reader.readAsDataURL(file);
      });
    } catch (error) {
      console.error('企業画像アップロードエラー:', error);
      // モック用（開発用）
      return type === 'logo' ? previewLogo : previewBanner;
    }
  };
  
  // フォーム送信処理
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setLoading(true);
    setError(null);
    setSuccessMessage('');
    
    try {
      // 画像のアップロード処理
      let updatedLogoUrl = logoUrl;
      let updatedBannerUrl = bannerUrl;
      
      if (logoFile) {
        updatedLogoUrl = await uploadImage(logoFile, 'logo');
      }
      
      if (bannerFile) {
        updatedBannerUrl = await uploadImage(bannerFile, 'banner');
      }
      
      // プロフィール更新データ
      const profileUpdateData = {
        corporateName: formData.shop_name,
        description: formData.description,
        website: formData.website,
        location: formData.location,
        phoneNumber: formData.phone_number,
        industry: formData.industry,
        logoUrl: updatedLogoUrl,
        bannerUrl: updatedBannerUrl,
        businessHours: formData.businessHours,
        officialLine: formData.official_line,
        jobPostingUrl: formData.job_posting_url,
        prefecture_code: formData.prefecture_code,
        selectedCities: selectedCities
      };
      
      // プロフィール更新APIの呼び出し
      await axios.put(
        `${config.API_BASE_URL}/api/corporate/profile`,
        profileUpdateData,
        {
          headers: {
            Authorization: `Bearer ${token}`
          }
        }
      );
      
      // 現在のユーザー情報の更新（必要であれば）
      if (refreshUserInfo) {
        await refreshUserInfo();
      }
      
      setLoading(false);
      setSuccessMessage('プロフィールを更新しました');
      setNotification({
        open: true,
        type: 'success',
        message: 'プロフィール情報が正常に更新されました'
      });
      
      // ファイル選択状態のリセット
      setLogoFile(null);
      setBannerFile(null);
    } catch (err) {
      
      setError('プロフィール更新中にエラーが発生しました。');
      setLoading(false);
      setNotification({
        open: true,
        type: 'error',
        message: 'プロフィール情報の更新に失敗しました'
      });
    }
  };
  
  // 通知関連
  const handleCloseNotification = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setNotification({ ...notification, open: false });
  };

  if (loading && !Object.values(formData).some(value => value !== '')) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="corporate-profile-edit">
      <Typography variant="h4" component="h1" gutterBottom>
        企業プロフィール編集
      </Typography>
      
      {error && (
        <Alert severity="error" className="alert-message">
          {error}
        </Alert>
      )}
      
      {successMessage && (
        <Alert severity="success" className="alert-message">
          {successMessage}
        </Alert>
      )}
      
      <Paper className="profile-paper">
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* バナー画像プレビュー */}
            <Grid item xs={12} className="banner-container">
              {bannerUrl || previewBanner ? (
                <img src={previewBanner || bannerUrl} alt="バナープレビュー" className="banner-preview" />
              ) : (
                <Box className="banner-placeholder">
                  <Typography variant="body1">バナー画像</Typography>
                </Box>
              )}
              
              <Button
                variant="contained"
                component="label"
                startIcon={<CloudUploadIcon />}
                className="upload-banner-button"
              >
                バナーをアップロード
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={(e) => handleFileChange(e, 'banner')}
                />
              </Button>
            </Grid>
            
            {/* ロゴとプロフィール基本情報 */}
            <Grid item xs={12} md={3} className="logo-container">
              <Avatar
                src={previewLogo || logoUrl}
                alt="企業ロゴ"
                className="profile-logo"
              />
              <Button
                variant="outlined"
                component="label"
                size="small"
                className="upload-logo-button"
              >
                ロゴを変更
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={(e) => handleFileChange(e, 'logo')}
                />
              </Button>
            </Grid>
            
            <Grid item xs={12} md={9}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    label="店名"
                    name="shop_name"
                    value={formData.shop_name}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="企業紹介"
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    multiline
                    rows={4}
                  />
                </Grid>
              </Grid>
            </Grid>
            
            <Grid item xs={12}>
              <Divider />
              <Typography variant="h6" className="section-title">
                基本情報
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Webサイト"
                name="website"
                value={formData.website}
                onChange={handleChange}
                placeholder="https://example.com"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="所在地"
                name="location"
                value={formData.location}
                onChange={handleChange}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="連絡先メールアドレス"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                disabled
                helperText="メールアドレスはユーザー登録時のものが設定されています"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="電話番号"
                name="phone_number"
                value={formData.phone_number}
                onChange={handleChange}
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel id="industry-label">業種（複数選択可）</InputLabel>
                <Select
                  labelId="industry-label"
                  multiple
                  name="industry"
                  value={formData.industry}
                  onChange={handleIndustryChange}
                  input={<OutlinedInput label="業種（複数選択可）" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => (
                        <Chip key={value} label={value} />
                      ))}
                    </Box>
                  )}
                >
                  {industries.map((industry) => (
                    <MenuItem key={industry} value={industry}>
                      {industry}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>当てはまる業種を選択してください</FormHelperText>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="オフィシャルLINE URL"
                name="official_line"
                value={formData.official_line}
                onChange={handleChange}
                placeholder="https://line.me/..."
                helperText="LINEの公式アカウントURLを入力してください"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="求人ページ URL"
                name="job_posting_url"
                value={formData.job_posting_url}
                onChange={handleChange}
                placeholder="https://..."
                helperText="求人情報ページのURLを入力してください"
              />
            </Grid>
            
            {/* 都道府県選択 */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>都道府県</InputLabel>
                <Select
                  name="prefecture_code"
                  value={formData.prefecture_code || ''}
                  onChange={handleChange}
                  label="都道府県"
                >
                  <MenuItem value=""><em>選択してください</em></MenuItem>
                  {prefectures.map((prefecture) => (
                    <MenuItem key={prefecture.code} value={prefecture.code}>
                      {prefecture.name}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>店舗がある都道府県を選択してください</FormHelperText>
              </FormControl>
            </Grid>
 
            {/* 市区町村選択 */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth disabled={!formData.prefecture_code}>
                <InputLabel>市区町村 (複数選択可)</InputLabel>
                <Select
                  multiple
                  value={selectedCities}
                  onChange={handleCityChange}
                  input={<OutlinedInput label="市区町村 (複数選択可)" />}
                  renderValue={(selected) => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map((value) => {
                        const city = cities.find(c => c.code === value);
                        return (
                          <Chip key={value} label={city ? city.name : value} />
                        );
                      })}
                    </Box>
                  )}
                >
                  {cities.map((city) => (
                    <MenuItem key={city.code} value={city.code}>
                      {city.name}
                    </MenuItem>
                  ))}
                </Select>
                <FormHelperText>サービス提供エリアを選択してください</FormHelperText>
              </FormControl>
            </Grid>

            {/* 営業時間 */}
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                営業時間
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="営業開始時間"
                    name="businessHours.start"
                    type="time"
                    value={formData.businessHours?.start || '10:00'}
                    onChange={handleChange}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="営業終了時間"
                    name="businessHours.end"
                    type="time"
                    value={formData.businessHours?.end || '22:00'}
                    onChange={handleChange}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
            
            <Grid item xs={12} className="submit-container">
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                startIcon={<SaveIcon />}
                disabled={loading}
              >
                {loading ? '保存中...' : '変更を保存'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
      
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.type} 
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CorporateProfileEdit; 